import React from 'react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { ChevronLeft, RefreshCw, Search, Filter, BarChart3, TrendingUp, Users, FileText, CreditCard, Award } from 'lucide-react';

/**
 * Componente de navegación avanzada para el dashboard de compañía
 * Incluye breadcrumb, controles de búsqueda, filtros y navegación por tabs
 */
const CompanyDashboardNavigation = ({
  breadcrumb = [],
  onNavigate,
  onDrillUp,
  onRefresh,
  currentLevel,
  isLoading = false,
  searchTerm,
  onSearchChange,
  filterOptions = [],
  selectedFilter,
  onFilterChange,
  showControls = true,
  showBreadcrumb = true,
  showTitle = true,
  activeTab = 'overview',
  onTabChange,
  showTabs = true,
  availableTabs = ['overview', 'products', 'plans', 'sessions', 'quotations', 'contracts', 'certificates', 'subscriptions']
}) => {
  
  const tabConfig = {
    overview: { label: 'Resumen', icon: BarChart3 },
    products: { label: 'Productos', icon: TrendingUp },
    plans: { label: 'Planes', icon: FileText },
    sessions: { label: 'Sesiones', icon: Users },
    quotations: { label: 'Cotizaciones', icon: FileText },
    contracts: { label: 'Contrataciones', icon: CreditCard },
    certificates: { label: 'Certificados', icon: Award },
    subscriptions: { label: 'Suscripciones', icon: CreditCard }
  };
  const renderBreadcrumb = () => {
    if (!showBreadcrumb || !breadcrumb || breadcrumb.length === 0) {
      return null;
    }

    return (
      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
        {breadcrumb.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="text-muted-foreground/50">/</span>}
            <button
              onClick={() => onNavigate && onNavigate(item)}
              className="hover:text-foreground transition-colors font-medium"
              disabled={index === breadcrumb.length - 1}
            >
              {item.label || item.name}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderTabs = () => {
    if (!showTabs || !availableTabs || availableTabs.length === 0) {
      return null;
    }

    return (
      <Tabs value={activeTab} onValueChange={onTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:grid-cols-8">
          {availableTabs.map((tab) => {
            const config = tabConfig[tab];
            if (!config) return null;
            
            const Icon = config.icon;
            return (
              <TabsTrigger 
                key={tab} 
                value={tab}
                className="flex items-center space-x-2 text-xs lg:text-sm"
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{config.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>
    );
  };

  const renderControls = () => {
    if (!showControls) {
      return null;
    }

    return (
      <div className="flex items-center space-x-4">
        {/* Búsqueda */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={searchTerm || ''}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
            className="pl-10 w-64"
          />
        </div>

        {/* Filtros */}
        {filterOptions.length > 0 && (
          <Select value={selectedFilter || ''} onValueChange={onFilterChange}>
            <SelectTrigger className="w-48">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filtrar por..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">Todos</SelectItem>
              {filterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Botón de actualizar */}
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>

        {/* Botón de retroceder */}
        {breadcrumb && breadcrumb.length > 1 && (
          <Button
            variant="outline"
            size="sm"
            onClick={onDrillUp}
            disabled={isLoading}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Atrás
          </Button>
        )}
      </div>
    );
  };

  const renderLevelIndicator = () => {
    if (!currentLevel) return null;

    return (
      <div className="flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Nivel:</span>
        <span className="font-medium bg-primary/10 text-primary px-2 py-1 rounded-md">
          {currentLevel}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header con breadcrumb y controles */}
      <div className="space-y-4">
        {/* Breadcrumb */}
        {renderBreadcrumb()}

        {/* Controles principales */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {showTitle && (
              <h2 className="text-xl font-bold">Dashboard de Compañía</h2>
            )}
            {renderLevelIndicator()}
          </div>
          {renderControls()}
        </div>
      </div>

      {/* Navegación por tabs */}
      {renderTabs()}
    </div>
  );
};

export default CompanyDashboardNavigation;